import { z } from 'zod'

export const issueCategories = [
  'general',
  'navigation',
  'data-display',
  'performance',
  'other',
] as const

export const issueRoutes = [
  'overview',
  'conversations',
  'patients',
  'appointments',
  'calls',
  'settings',
  'other',
] as const

export const issueAttachmentTypes = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

export const issueAttachmentMaxBytes = 2 * 1024 * 1024

const uuidV4Pattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const fullShaPattern = /^[0-9a-f]{40}$/
const base64Pattern =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

export const issueReportAttachmentSchema = z
  .object({
    contentType: z.enum(issueAttachmentTypes),
    sizeBytes: z.number().int().positive().max(issueAttachmentMaxBytes),
    base64Data: z
      .string()
      .min(4)
      .max(Math.ceil(issueAttachmentMaxBytes / 3) * 4 + 4)
      .regex(base64Pattern),
  })
  .strict()

export const issueReportPayloadSchema = z
  .object({
    category: z.enum(issueCategories),
    subject: z
      .string()
      .trim()
      .min(5, 'Subject must be at least 5 characters.')
      .max(80, 'Subject must be 80 characters or fewer.'),
    description: z
      .string()
      .trim()
      .min(10, 'Description must be at least 10 characters.')
      .max(1000, 'Description must be 1000 characters or fewer.'),
    route: z.enum(issueRoutes),
    clientRequestId: z.string().regex(uuidV4Pattern),
    deploymentSha: z.string().regex(fullShaPattern).nullable(),
    attachment: issueReportAttachmentSchema.optional(),
  })
  .strict()

export const issueReportResponseSchema = z
  .object({
    issueId: z.string().regex(/^[0-9a-f]{64}$/),
    status: z.literal('submitted'),
    duplicate: z.boolean(),
  })
  .strict()

export const deploymentMetadataSchema = z
  .object({
    application: z.literal('flio-dashboard'),
    environment: z.string(),
    firebaseProject: z.string(),
    hostingSite: z.string(),
    sourceSha: z.string().regex(fullShaPattern),
  })
  .strict()

export type IssueCategory = (typeof issueCategories)[number]
export type IssueRoute = (typeof issueRoutes)[number]
export type IssueReportPayload = z.infer<typeof issueReportPayloadSchema>
export type IssueReportAttachment = z.infer<typeof issueReportAttachmentSchema>
export type IssueReportResponse = z.infer<typeof issueReportResponseSchema>

const routeByPathname: Record<string, IssueRoute> = {
  '/': 'overview',
  '/conversations': 'conversations',
  '/patients': 'patients',
  '/appointments': 'appointments',
  '/calls': 'calls',
  '/settings': 'settings',
}

export function mapDashboardRoute(pathname: unknown): IssueRoute {
  return typeof pathname === 'string'
    ? (routeByPathname[pathname] ?? 'other')
    : 'other'
}
